import { useEffect, useState, useMemo, useRef } from "react";
import { Building2, ChevronDown, Clock, FolderOpen, Search, Check, RefreshCw } from "lucide-react";
import { useTenants } from "../../hooks/useTenants";
import { myLocalStorage } from "../StorageHelper";
import { AWSAccount, AzureSubscription } from "./Cloud/interface";
import {
    awsAccountList,
    awsTenantAccountList,
    azureSubcriptionList,
    gcpProjectList,
} from "./Cloud/Data";
import useUserStore from "../../services/userStore";
import { Button } from "../ui/button";

// ─── Interfaces ─────────────────────────────────────────

export interface CloudFiltersProps {
    cloudProvider: string;
    onTenantChange: (tenant: string) => void;
    onCloudAccountChange: (value: string) => void;
    isAdmin?: boolean;
    // Search
    searchQuery?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    // Sync
    onSync?: () => void;
    syncDisabled?: boolean;
    syncLoading?: boolean;
    // Time Filter
    showTimeFilter?: boolean;
    timeRangeValue?: string;
    onTimeRangeChange?: (value: string) => void;
    timeRanges?: { value: string; label: string }[];
}

// ─── Generic Dropdown ───────────────────────────────────

interface FilterDropdownProps {
    id: string;
    items: { value: string; label: string; meta?: string }[];
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    searchPlaceholder?: string;
    loading?: boolean;
    emptyText?: string;
    renderItem?: (item: { value: string; label: string; meta?: string }, isSelected: boolean) => React.ReactNode;
}

function FilterDropdown({
    id,
    items,
    value,
    onChange,
    placeholder,
    searchPlaceholder = "Search…",
    loading = false,
    emptyText = "Nothing found",
    renderItem,
}: FilterDropdownProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handle = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, [open]);

    // Focus search when opened
    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 10);
    }, [open]);

    const filtered = useMemo(() => {
        const term = search.toLowerCase();
        return items.filter((i) => i.label.toLowerCase().includes(term));
    }, [items, search]);

    const selectedLabel = items.find((i) => i.value === value)?.label || "";

    if (loading) {
        return (
            <div className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-card border border-border/50 shadow-sm animate-pulse">
                <div className="h-4 bg-muted/60 rounded w-2/3" />
                <div className="h-4 w-4 bg-muted/60 rounded flex-shrink-0" />
            </div>
        );
    }

    return (
        <div ref={containerRef} className={`relative w-full ${open ? "z-[9999]" : "z-40"}`}>
            <button
                id={id}
                type="button"
                className="w-full group flex items-center justify-between px-4 py-2.5 rounded-xl bg-card border border-border/50 hover:border-sky-500/30 transition-all duration-300 shadow-sm cursor-pointer"
                onClick={() => setOpen((o) => !o)}
            >
                <span className="text-[14px] font-medium text-foreground group-hover:text-primary transition-colors truncate">
                    {selectedLabel || placeholder}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-y-0.5 flex-shrink-0 ml-2" />
            </button>

            {open && (
                <div className="absolute top-[calc(100%+8px)] left-0 min-w-[240px] w-full bg-popover/95 backdrop-blur-xl border border-border/50 shadow-xl shadow-sky-500/10 rounded-xl overflow-hidden z-[9999]">
                    {items.length > 4 && (
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50 bg-muted/20">
                            <Search className="w-4 h-4 text-muted-foreground" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={searchPlaceholder}
                                className="w-full bg-transparent outline-none border-none text-sm text-foreground placeholder:text-muted-foreground focus:ring-0"
                            />
                        </div>
                    )}

                    <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-1.5 space-y-0.5">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-4 text-[13px] text-muted-foreground text-center bg-muted/20 rounded-lg m-1">{emptyText}</div>
                        ) : (
                            filtered.map((item) => {
                                const isSelected = item.value === value;
                                return (
                                    <div
                                        key={item.value}
                                        className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${isSelected
                                                ? "bg-gradient-to-r from-primary/20 to-primary/10 text-sky-500"
                                                : "hover:bg-accent/50 text-foreground font-medium"
                                            }`}
                                        onClick={() => {
                                            onChange(item.value);
                                            setOpen(false);
                                            setSearch("");
                                        }}
                                    >
                                        <div className="flex-1 truncate font-medium">
                                            {renderItem ? renderItem(item, isSelected) : item.label}
                                        </div>
                                        {isSelected && (
                                            <div className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 flex-shrink-0 shadow-sm">
                                                <Check className="h-3 w-3 text-primary-foreground" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Cloud Account helpers (mirrored from CloudAccountList) ──

function getLabel(provider: string): string {
    switch (provider.toLowerCase()) {
        case "gcp": return "Project";
        case "aws": return "Account";
        case "azure": return "Subscription";
        default: return "Resource";
    }
}

function getDisplayName(item: string | AWSAccount | AzureSubscription, provider: string): string {
    if (!item) return "-";
    if (provider === "gcp") return typeof item === "string" ? item : "-";
    if (provider === "aws") {
        const aws = item as AWSAccount;
        return aws.accountName || aws.awsAccountName || aws.accountId || aws.awsAccountId || "-";
    }
    if (provider === "azure") {
        const azure = item as AzureSubscription;
        return azure.subscriptionName || azure.azureSubscriptionId || "-";
    }
    return "-";
}

function getUniqueId(item: string | AWSAccount | AzureSubscription, provider: string): string {
    if (provider === "gcp") return typeof item === "string" ? item : "";
    if (provider === "aws") {
        const aws = item as AWSAccount;
        return aws.accountId || aws.awsAccountId || "";
    }
    if (provider === "azure") return (item as AzureSubscription).azureSubscriptionId;
    return "";
}

// ─── Main Component ─────────────────────────────────────

export default function CloudFilters({
    cloudProvider,
    onTenantChange,
    onCloudAccountChange,
    isAdmin = false,
    searchQuery = "",
    onSearchChange,
    searchPlaceholder = "Search…",
    onSync,
    syncDisabled = false,
    syncLoading = false,
    showTimeFilter = false,
    timeRangeValue = "",
    onTimeRangeChange,
    timeRanges = [],
}: CloudFiltersProps) {
    const { email } = useUserStore((state) => state.user || {});
    const { tenants, loading: tenantsLoading } = useTenants();

    // Tenant state
    const [selectedTenant, setSelectedTenant] = useState("");

    // Cloud account state
    const [cloudItems, setCloudItems] = useState<(string | AWSAccount | AzureSubscription)[]>([]);
    const [selectedCloudAccount, setSelectedCloudAccount] = useState("");
    const [cloudLoading, setCloudLoading] = useState(false);

    // Combined loading: all dropdowns show shimmer until everything is ready
    const isLoading = tenantsLoading || cloudLoading || (tenants.length > 0 && !selectedTenant);

    // ── Tenant initialization ───────────────────────────
    useEffect(() => {
        if (tenants.length === 0) return;
        const latestTenant = myLocalStorage.getItem("latestTenant");
        const existing = tenants.find((t) => t.tenantName === latestTenant?.tenantName || t.tenantName === latestTenant);

        if (existing) {
            myLocalStorage.setItem("latestTenant", existing);
            setCloudLoading(true);
            setSelectedTenant(existing.tenantName);
            onTenantChange(existing.tenantName);
        } else {
            const first = tenants[0];
            myLocalStorage.setItem("latestTenant", first);
            setCloudLoading(true);
            setSelectedTenant(first.tenantName);
            onTenantChange(first.tenantName);
        }
    }, [tenants]);

    // ── Cloud account fetching ──────────────────────────
    useEffect(() => {
        if (!selectedTenant) return;

        const fetchCloudAccounts = async () => {
            setCloudLoading(true);
            try {
                let data: (string | AWSAccount | AzureSubscription)[] = [];
                const cp = cloudProvider.toLowerCase();

                if (cp === "gcp") {
                    data = await gcpProjectList();
                } else if (cp === "aws") {
                    data = isAdmin
                        ? await awsAccountList(email)
                        : await awsTenantAccountList(email);
                } else if (cp === "azure") {
                    data = await azureSubcriptionList();
                }

                setCloudItems(Array.isArray(data) ? data : []);

                // Restore selection from localStorage
                let storedValue: string | null = null;
                if (cp === "gcp") {
                    storedValue = myLocalStorage.getItem("gcpSelectedProjectId");
                } else if (cp === "aws") {
                    storedValue = myLocalStorage.getItem("selectedAwsAccount");
                } else if (cp === "azure") {
                    const azureStored = myLocalStorage.getItem("latestSubList");
                    if (azureStored) {
                        try {
                            const obj = JSON.parse(azureStored);
                            storedValue = obj?.azureSubscriptionId ?? null;
                        } catch {
                            storedValue = null;
                        }
                    }
                }

                let selectedItem: string | AWSAccount | AzureSubscription | null = null;
                if (storedValue) {
                    selectedItem = (data as any[]).find(
                        (item) => getUniqueId(item, cloudProvider) === storedValue
                    );
                }

                // Always fallback to first item
                if (!selectedItem && data.length > 0) {
                    selectedItem = data[0];
                }

                if (selectedItem) {
                    const uniqueId = getUniqueId(selectedItem, cloudProvider);
                    const displayName = getDisplayName(selectedItem, cloudProvider);
                    setSelectedCloudAccount(uniqueId);
                    persistCloudSelection(uniqueId, displayName);
                    onCloudAccountChange(uniqueId);
                }
            } catch (err) {
                console.error(`Error loading ${cloudProvider} resources:`, err);
                setCloudItems([]);
            } finally {
                setCloudLoading(false);
            }
        };

        fetchCloudAccounts();
    }, [cloudProvider, email, isAdmin, selectedTenant]);

    // ── Persist cloud selection ─────────────────────────
    const persistCloudSelection = (uniqueId: string, displayName: string) => {
        const cp = cloudProvider.toLowerCase();
        if (cp === "gcp") {
            myLocalStorage.setItem("gcpSelectedProjectId", uniqueId);
        } else if (cp === "aws") {
            myLocalStorage.setItem("selectedAwsAccount", uniqueId);
        } else if (cp === "azure") {
            myLocalStorage.setItem(
                "latestSubList",
                JSON.stringify({
                    azureSubscriptionId: uniqueId,
                    subscriptionName: displayName,
                })
            );
        }
    };

    // ── Handlers ────────────────────────────────────────
    const handleTenantChange = (tenantName: string) => {
        const tenant = tenants.find((t) => t.tenantName === tenantName);
        if (tenant) {
            myLocalStorage.setItem("latestTenant", tenant);
        }
        setCloudLoading(true);
        setSelectedTenant(tenantName);
        onTenantChange(tenantName);
    };

    const handleCloudAccountChange = (uniqueId: string) => {
        const item = (Array.isArray(cloudItems) ? cloudItems : []).find(
            (i: any) => getUniqueId(i, cloudProvider) === uniqueId
        );
        const displayName = item ? getDisplayName(item, cloudProvider) : uniqueId;
        setSelectedCloudAccount(uniqueId);
        persistCloudSelection(uniqueId, displayName);
        onCloudAccountChange(uniqueId);
    };

    // ── Build dropdown items ────────────────────────────
    const tenantItems = tenants.map((t) => ({
        value: t.tenantName,
        label: t.tenantName,
    }));

    const cloudAccountItems = (Array.isArray(cloudItems) ? cloudItems : []).map((item: any) => ({
        value: getUniqueId(item, cloudProvider),
        label: getDisplayName(item, cloudProvider),
    }));

    // ── Render ──────────────────────────────────────────
    return (
        <div className="relative z-50 w-full flex items-center p-3 rounded-2xl bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-xl border border-border/50 shadow-lg shadow-sky-500/5 gap-4 lg:gap-6 flex-wrap mb-6">
            {/* Tenant */}
            <div className="flex flex-col gap-1.5 min-w-[180px] w-[240px]">
                <div className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1.5 px-1">
                    <Building2 className="w-3.5 h-3.5" />
                    Tenant
                </div>
                <FilterDropdown
                    id="cf-tenant"
                    items={tenantItems}
                    value={selectedTenant}
                    onChange={handleTenantChange}
                    placeholder="Select tenant"
                    searchPlaceholder="Search tenants…"
                    loading={isLoading}
                    emptyText="No tenants found"
                />
            </div>

            <div className="hidden sm:block w-px h-12 bg-gradient-to-b from-transparent via-border to-transparent" />

            {/* Cloud Account */}
            <div className="flex flex-col gap-1.5 min-w-[180px] w-[240px]">
                <div className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1.5 px-1">
                    <FolderOpen className="w-3.5 h-3.5" />
                    {getLabel(cloudProvider)}
                </div>
                <FilterDropdown
                    id="cf-cloud-account"
                    items={cloudAccountItems}
                    value={selectedCloudAccount}
                    onChange={handleCloudAccountChange}
                    placeholder={`Select ${getLabel(cloudProvider).toLowerCase()}`}
                    searchPlaceholder={`Search ${getLabel(cloudProvider).toLowerCase()}s…`}
                    loading={isLoading}
                    emptyText="No resources found"
                />
            </div>

            {/* Search */}
            {onSearchChange && (
                <>
                    <div className="hidden lg:block w-px h-12 bg-gradient-to-b from-transparent via-border to-transparent" />
                    <div className="flex flex-col gap-1.5 flex-1 min-w-[200px]">
                        <div className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1.5 px-1">
                            <Search className="w-3.5 h-3.5" />
                            Search
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card border border-border/50 hover:border-sky-500/30 transition-all duration-300 focus-within:ring-1 focus-within:ring-sky-500/50 shadow-sm">
                            <Search className="w-4 h-4 text-muted-foreground" />
                            <input
                                type="text"
                                className="w-full bg-transparent outline-none border-none text-sm font-medium text-foreground placeholder:text-muted-foreground"
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => onSearchChange(e.target.value)}
                            />
                        </div>
                    </div>
                </>
            )}

            {/* Time Filter */}
            {showTimeFilter && onTimeRangeChange && timeRanges.length > 0 && (
                <>
                    <div className="hidden lg:block w-px h-12 bg-gradient-to-b from-transparent via-border to-transparent" />
                    <div className="flex flex-col gap-1.5 min-w-[180px] w-[200px]">
                        <div className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider flex items-center gap-1.5 px-1">
                            <Clock className="w-3.5 h-3.5" />
                            Time Range
                        </div>
                        <FilterDropdown
                            id="cf-time-range"
                            items={timeRanges}
                            value={timeRangeValue}
                            onChange={onTimeRangeChange}
                            placeholder="Select time range"
                            searchPlaceholder="Search ranges…"
                        />
                    </div>
                </>
            )}

            {/* Sync */}
            {onSync && (
                <>
                    <div className="hidden xl:block w-px h-12 bg-gradient-to-b from-transparent via-border to-transparent" />
                    <div className="flex flex-col justify-end mt-5">
                        <Button
                            onClick={onSync}
                            disabled={syncDisabled}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all duration-300 ${syncDisabled ? 'opacity-50 cursor-not-allowed' : 'shadow-md shadow-primary/20 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/30'}`}
                            size="default"
                        >
                            <RefreshCw className={`w-4 h-4 ${syncLoading ? "animate-spin" : ""}`} />
                            <span className="text-[13px] tracking-wide uppercase">Sync</span>
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
